import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  COUNTRY_OPTIONS,
  FLOOR_OPTIONS,
  OBJECT_TYPE_OPTIONS,
  ROOM_OPTIONS,
} from '@webapp/core/constants/inquiry-labels';
import { CreateInquiryStep2 } from '@webapp/core/interfaces';
import { ToastrService } from 'ngx-toastr';
import { GooglePlacesService } from 'projects/webapp/app/services/google-places.service';
import {
  InquiryDraftSource,
  PublicInquiryManagerService,
} from 'projects/webapp/app/services/public-inquiry-manager.service';


/** Auszug: the address a move starts from, and the size of the home. */
@Component({
  selector: 'app-inquiry-step2',
  templateUrl: './step2.component.html',
})
export class InquiryStep2Component implements OnInit, OnDestroy {
  draftInquiryForm = new FormGroup({
    address: new FormControl('', { validators: Validators.required, nonNullable: true }),
    zip: new FormControl('', { validators: Validators.required, nonNullable: true }),
    city: new FormControl('', { validators: Validators.required, nonNullable: true }),
    country: new FormControl('CH', { validators: Validators.required, nonNullable: true }),
    object_type: new FormControl('', { validators: Validators.required, nonNullable: true }),
    floor: new FormControl('', { validators: Validators.required, nonNullable: true }),
    has_lift: new FormControl<boolean | null>(null, { validators: Validators.required }),

    rooms: new FormControl('', { validators: Validators.required, nonNullable: true }),
    square_meters: new FormControl(
      '',
      { validators: [Validators.required, Validators.min(10), Validators.max(500)], nonNullable: true }
    ),
  });

  objectTypeOptions = OBJECT_TYPE_OPTIONS;
  floorOptions = FLOOR_OPTIONS;
  roomOptions = ROOM_OPTIONS;
  countryOptions = COUNTRY_OPTIONS;

  protected _draftSource: InquiryDraftSource = () => ({ step2: this._buildPayload() });

  constructor(
    protected _toastrService: ToastrService,
    protected _publicInquiryManagerService: PublicInquiryManagerService,
    private _googlePlacesService: GooglePlacesService,
  ) {
  }

  ngOnInit() {
    this._loadFormData();
    this._publicInquiryManagerService.setDraftSource(this._draftSource);
  }

  ngOnDestroy() {
    this._publicInquiryManagerService.clearDraftSource(this._draftSource);
  }

  onSubmit() {
    this.draftInquiryForm.markAllAsTouched();
    if (!this.draftInquiryForm.valid) {
      this._toastrService.warning('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }

    this._publicInquiryManagerService.submitStep2(this._buildPayload());
  }

  hasError(controlName: string, errorName?: string) {
    const control = this.draftInquiryForm.get(controlName);
    const isValid = errorName ? !control?.hasError(errorName) : !control?.invalid;
    return !!control?.touched && !isValid;
  }

  isHasLiftOptionSelected(value: boolean | null = null) {
    return this.draftInquiryForm.controls.has_lift.value === value;
  }

  selectHasLift(value: boolean) {
    this.draftInquiryForm.controls.has_lift.setValue(value);
  }

  isObjectTypeActive(value: string) {
    return this.draftInquiryForm.controls.object_type.value === value;
  }

  selectObjectType(value: string) {
    this.draftInquiryForm.controls.object_type.setValue(value);
  }

  goBack() {
    this._publicInquiryManagerService.goPrevStep()
  }

  onAddressPlaceSelected(place: google.maps.places.Place) {
    if (!place.addressComponents) return;

    const get = (type: string) =>
      place.addressComponents?.find((c: google.maps.places.AddressComponent) => c.types.includes(type))?.longText ?? '';

    const streetNumber = get('street_number');
    const route = get('route');
    const postalCode = get('postal_code');
    const locality = get('locality');

    const streetParts = [route, streetNumber].filter(el => !!el);
    const address = streetParts.join(' ') || place.formattedAddress?.split(',')[0]?.trim() || '';

    this.draftInquiryForm.patchValue({
      address,
      ...(postalCode ? { zip: postalCode } : {}),
      ...(locality ? { city: locality } : {}),
    });
  }

  async onZipPlaceSelected(place: google.maps.places.Place) {
    if (!place.addressComponents) return;

    const get = (type: string) =>
      place.addressComponents?.find((c: google.maps.places.AddressComponent) => c.types.includes(type))?.longText ?? '';

    const postalCode = get('postal_code');
    const locality = get('locality');

    this.draftInquiryForm.patchValue({ zip: postalCode, city: locality });

    const currentAddress = this.draftInquiryForm.controls.address.value;
    if (currentAddress && locality) {
      const country = this.draftInquiryForm.controls.country.value.toLowerCase();
      const found = await this._googlePlacesService.hasAddressInCity(currentAddress, locality, country);
      if (!found) this.draftInquiryForm.controls.address.setValue('');
    }
  }

  async onCityPlaceSelected(place: google.maps.places.Place) {
    if (!place.addressComponents) return;

    const get = (type: string) =>
      place.addressComponents?.find((c: google.maps.places.AddressComponent) => c.types.includes(type))?.longText ?? '';

    const locality = get('locality');
    let postalCode = get('postal_code');

    if (locality && !postalCode) {
      const currentPostalCode = this.draftInquiryForm.controls.zip.value;
      const found = await this._googlePlacesService.hasCityPostalCode(currentPostalCode, locality);
      postalCode = found ? currentPostalCode : '';
    }
    this.draftInquiryForm.patchValue({ city: locality, zip: postalCode });

    const currentAddress = this.draftInquiryForm.controls.address.value;
    if (currentAddress && locality) {
      const country = this.draftInquiryForm.controls.country.value.toLowerCase();
      const found = await this._googlePlacesService.hasAddressInCity(currentAddress, locality, country);
      if (!found) this.draftInquiryForm.controls.address.setValue('');
    }
  }

  protected _loadFormData() {
    const { step2 } = this._publicInquiryManagerService.inquiryCreationProgress ?? {};
    if (step2) {
      this.draftInquiryForm.patchValue({
        address: step2.from_address,
        zip: step2.from_zip,
        city: step2.from_city,
        country: step2.from_country,
        object_type: step2.from_object_type,
        floor: step2.from_floor,
        has_lift: step2.from_has_lift,
        rooms: step2.rooms,
        square_meters: step2.square_meters,
      });
    }
  }

  protected _buildPayload(): CreateInquiryStep2 {
    const v = this.draftInquiryForm.getRawValue();
    return {
      from_address: v.address,
      from_zip: v.zip,
      from_city: v.city,
      from_country: v.country,
      from_object_type: v.object_type,
      from_floor: v.floor,
      from_has_lift: v.has_lift,
      rooms: v.rooms,
      square_meters: v.square_meters,
    };
  }
}
